import { isEqual, snakeCase } from 'lodash-es'

import type { ApiOptions } from './interfaces'
import { createHighLevelApi } from '@sdk/api/HighLevelApi'
import ui from '@sdk/api/modules/ui'
import admin from '@sdk/api/modules/admin'
import dsr from '@sdk/api/modules/dsr'

export * from '@sdk/interfaces'
export * from '@sdk/payload.types'
export * from '@sdk/api/modules/ui/types.ui'
export * from '@sdk/api/modules/admin/types.admin'
export * from '@sdk/api/modules/dsr/types.dsr'
export * from '@sdk/peer-connectivity'

export let highLevelApi: ReturnType<typeof createHighLevelApi>

let prevOptions: ApiOptions | undefined

/**
 * Check out the returned object documentation for the list of available methods.
 */
export function usePitcherApi(options?: ApiOptions): ReturnType<typeof createHighLevelApi> {
  if (!highLevelApi || !isEqual(options, prevOptions)) {
    highLevelApi = createHighLevelApi(options)
    prevOptions = options
  }

  return highLevelApi
}

function enhanceWithSnakeCase<T extends Record<string, any>>(api: T): T {
  Object.entries(api).forEach(([key, value]) => {
    const newKey = snakeCase(key)
    if (newKey && !api[newKey]) {
      ;(api as any)[newKey] = value
    }
  })
  return api
}

const uiApi = enhanceWithSnakeCase(ui)
const adminApi = enhanceWithSnakeCase(admin)
const dsrApi = enhanceWithSnakeCase(dsr)

/**
 * API methods available in the CatalogIQ default UI.
 *
 * You can store the API object in a variable and call the methods directly.
 *
 * Most methods have a `snake_case` and `camelCase` version of their name available.
 *
 * ```ts
 * const uiApi = useUi()
 * ```
 *
 * @returns UI API object with methods described below.
 */
export function useUi() {
  return uiApi
}

/**
 * API methods available in the CatalogIQ Admin.
 *
 * You can store the API object in a variable and call the methods directly.
 *
 * Most methods have a `snake_case` and `camelCase` version of their name available.
 *
 * ```ts
 * import { useAdmin } from '@pitcher/js-api'
 *
 * const adminApi = useAdmin()
 * ```
 */
export function useAdmin() {
  return adminApi
}

/**
 * API methods available in the CatalogIQ default UI.
 *
 * You can store the API object in a variable and call the methods directly.
 *
 * Most methods have a `snake_case` and `camelCase` version of their name available.
 *
 * ```ts
 * import { useDsr } from '@pitcher/js-api'
 *
 * const dsrApi = useDsr()
 * ```
 */
export function useDsr() {
  return dsrApi
}

export type JsApiLocation = 'ui' | 'admin' | 'dsr' | 'impact'

export function getAvailableApis(): JsApiLocation[] {
  if (typeof window === 'undefined') throw new Error('getAvailableApis: window is not defined')
  const location = window.parent.jsApiLocation
  return location === 'ui' ? ['ui', 'impact'] : [location ?? 'impact']
}

/**
 * This method returns an object containing the API methods that are available to you in a given environment, whether that be the Canvas, UI, Admin or DSR.
 *
 * - CatalogIQ UI - the method will return an object containing methods available in both `usePitcherApi` and `useUi`
 * - CatalogIQ DSR - the method will return the same result as `useDsr`
 * - CatalogIQ Admin - the method will return the same result as `useAdmin`
 *
 * ```ts
 * import { useApi } from '@pitcher/js-api'
 *
 * const api = useApi()
 * ```
 */
export function useApi(options?: ApiOptions) {
  if (typeof window === 'undefined') throw new Error('useApi: window is not defined')
  const location = window.parent.jsApiLocation
  switch (location) {
    case 'ui':
      return { ...usePitcherApi(), ...useUi(), ui: useUi(), impact: usePitcherApi() } // merge, but also expose as separate keys to avoid collisions
    case 'admin':
      return useAdmin()
    case 'dsr':
      return { ...useDsr(), broadcast: usePitcherApi().broadcast }
    case 'impact':
      return usePitcherApi(options) // first level (slotty and custom UIs)
    default:
      return usePitcherApi(options)
  }
}
